/**
 * Program Access Policy — shared types
 *
 * A Program carries `accessPolicy: { floor, allowUpgrades }` which drives the
 * adaptive sign-in ladder shown to participants on the public-program flow.
 *
 *   floor: 'off'      → anonymous join is the PRIMARY action (one tap)
 *   floor: 'verified' → name + email/phone + OTP (the express rung) is PRIMARY
 *   floor: 'on'       → full account only
 *
 *   allowUpgrades: when true, the dialog offers higher rungs as secondary
 *     affordances (e.g. "Verify with email" / "Create an account").
 */

export type AccessPolicyFloor = 'off' | 'verified' | 'on';

export interface AccessPolicy {
  floor: AccessPolicyFloor;
  allowUpgrades: boolean;
}

/**
 * The default policy applied when a Program has no `accessPolicy` set.
 * Mirrors the historical behavior: legacy (and new-but-unconfigured) programs
 * required a full account to enrol, so the default floor is 'on'. A creator
 * must explicitly lower the dial (Verified / Off) to enable lighter rungs.
 * (allowUpgrades is moot at floor 'on' — there is no higher rung to offer.)
 */
export const DEFAULT_ACCESS_POLICY: AccessPolicy = {
  floor: 'on',
  allowUpgrades: true
};

/**
 * Normalize a raw/partial accessPolicy from a Program doc into a complete,
 * safe AccessPolicy. Missing or malformed values fall back to the default.
 */
export function normalizeAccessPolicy(raw: unknown): AccessPolicy {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_ACCESS_POLICY };
  const r = raw as Partial<AccessPolicy>;
  const floor: AccessPolicyFloor =
    r.floor === 'off' || r.floor === 'verified' || r.floor === 'on'
      ? r.floor
      : DEFAULT_ACCESS_POLICY.floor;
  const allowUpgrades =
    typeof r.allowUpgrades === 'boolean' ? r.allowUpgrades : DEFAULT_ACCESS_POLICY.allowUpgrades;
  return { floor, allowUpgrades };
}
